import { Dispatch, SetStateAction, memo } from 'react';
import { ActionIcon, Grid, Tooltip } from '@mantine/core';
import { type MRT_PaginationState } from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { IconEye } from '@tabler/icons-react';
import { formatMoneyString } from '../../../utils/strings';
import Table from '../../../components/Table';
import { ICashUpReport, ICashierBox } from '../../../interfaces/Store';
import moment from 'moment';
import {
  buildCashUpReportDisplaySummary,
  type CashupPaymentTypeKey
} from '../../../utils/cashUpReportSummary';

const formatCashupAmount = (value: number | string) =>
  typeof value === 'number' ? formatMoneyString(value.toFixed(2)) : value;

const buildPaymentHeader = (
  t: ReturnType<typeof useTranslation>['t'],
  paymentType: CashupPaymentTypeKey,
  valueType: 'calculated' | 'counted' | 'difference'
) => `${t(`CashupsPage:cashUpReport.${paymentType}`)} ${t(`CashupsPage:cashUpReport.${valueType}`)}`;

const buildSummaryHeader = (
  t: ReturnType<typeof useTranslation>['t'],
  valueType: 'calculated' | 'counted' | 'difference'
) => `${t('CashupsPage:cashUpReport.total')} ${t(`CashupsPage:cashUpReport.${valueType}`)}`;

export default memo(function TableView({
  storeId = '',
  tableInfoData = [],
  totalRowsCount = 0,
  pages = 0,
  isError = false,
  isLoading = false,
  pagination,
  setPagination
}: {
  storeId: string;
  tableInfoData: ICashUpReport[];
  totalRowsCount: any;
  pages: any;
  isError: boolean;
  isLoading: boolean;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
}) {
  const { t } = useTranslation(['FieldName', 'CommonButtons', 'CashupsPage']);
  const history = useHistory();

  const columns = [
    {
      id: 'view',
      accessorFn: (row: ICashUpReport) => (
        <Tooltip label={t('CommonButtons:view')}>
          <ActionIcon
            aria-label={t('CommonButtons:view')}
            disabled={!row._id || !storeId}
            variant="subtle"
            onClick={() =>
              history.push(`/cashUp/${row._id}?storeId=${storeId}`, {
                storeID: storeId
              })
            }>
            <IconEye size={18} />
          </ActionIcon>
        </Tooltip>
      ),
      header: t('CommonButtons:view')
    },
    {
      id: 'cashierBox',
      accessorFn: (row: ICashUpReport) => (row.cashierBox as ICashierBox)?.name,
      header: t('FieldName:cashierBox')
    },
    {
      id: 'createdDate',
      accessorFn: (row: ICashUpReport) => moment(row.createdDate).format('DD/MM/YYYY hh:mm'),
      header: t('FieldName:createdDate')
    },
    {
      id: 'startingCashDate',
      accessorFn: (row: ICashUpReport) => moment(row.startingCashDate).format('DD/MM/YYYY hh:mm'),
      header: t('FieldName:startingCashDate')
    },
    {
      id: 'startingCash',
      accessorFn: (row: ICashUpReport) => formatMoneyString((row.startingCash || 0).toFixed(2)),
      header: t('FieldName:startingCash')
    },
    {
      id: 'leftInDrawer',
      accessorFn: (row: ICashUpReport) => formatMoneyString((row.leftInDrawer || 0).toFixed(2)),
      header: t('FieldName:leftInDrawer')
    },
    {
      id: 'cashToBank',
      accessorFn: (row: ICashUpReport) => formatMoneyString((row.cashToBank || 0).toFixed(2)),
      header: t('FieldName:cashToBank')
    },
    {
      id: 'calculatedTotal',
      accessorFn: (row: ICashUpReport) =>
        formatCashupAmount(buildCashUpReportDisplaySummary(row).calculatedTotal),
      header: buildSummaryHeader(t, 'calculated')
    },
    {
      id: 'declaredTotal',
      accessorFn: (row: ICashUpReport) =>
        formatCashupAmount(buildCashUpReportDisplaySummary(row).declaredTotal),
      header: buildSummaryHeader(t, 'counted')
    },
    {
      id: 'differenceTotal',
      accessorFn: (row: ICashUpReport) =>
        formatCashupAmount(buildCashUpReportDisplaySummary(row).difference),
      header: buildSummaryHeader(t, 'difference')
    },
    ...(['cash', 'card', 'transfer', 'mercadopago'] as CashupPaymentTypeKey[]).flatMap(
      (paymentType) => [
        {
          id: `${paymentType}Calculated`,
          accessorFn: (row: ICashUpReport) => {
            const summary = buildCashUpReportDisplaySummary(row);
            const paymentRow = summary.paymentTypeRows.find(({ key }) => key === paymentType);
            return formatCashupAmount(paymentRow?.calculated || 0);
          },
          header: buildPaymentHeader(t, paymentType, 'calculated')
        },
        {
          id: `${paymentType}Counted`,
          accessorFn: (row: ICashUpReport) => {
            const summary = buildCashUpReportDisplaySummary(row);
            const paymentRow = summary.paymentTypeRows.find(({ key }) => key === paymentType);
            return formatCashupAmount(paymentRow?.counted || 0);
          },
          header: buildPaymentHeader(t, paymentType, 'counted')
        },
        {
          id: `${paymentType}Difference`,
          accessorFn: (row: ICashUpReport) => {
            const summary = buildCashUpReportDisplaySummary(row);
            const paymentRow = summary.paymentTypeRows.find(({ key }) => key === paymentType);
            return formatCashupAmount(paymentRow?.difference || 0);
          },
          header: buildPaymentHeader(t, paymentType, 'difference')
        }
      ]
    ),
    {
      id: 'notes',
      accessorFn: (row: ICashUpReport) => row.notes,
      header: t('FieldName:notes')
    }
  ];

  return (
    <>
      <Grid gutter="md">
        <Grid.Col span={12}>
          <Table
            tableColumns={columns}
            pagination={pagination}
            setPagination={setPagination}
            tableData={tableInfoData}
            isError={isError}
            paginationIsLoading={isLoading}
            numPages={pages}
            totalRowsCount={totalRowsCount}
            overrideTableConfigs={{
              manualPagination: false
            }}
          />
        </Grid.Col>
      </Grid>
    </>
  );
});
